import {
  LastInTuple,
  LatestVersion,
  MigrationFunction,
  VersionedSchema,
  VersionedSchemaType,
  VersionSchemaInterface,
} from '@mutiny-pkg/versioned-schema/src/types';
import { z } from 'zod';

type VersionConfiguration<T extends VersionedSchema, Versions extends T[] = T[]> = {
  schema: T;
  migration?: Versions extends [] ? never : MigrationFunction<T, LastInTuple<Versions>>;
};

export class VersionedSchemaBuilder<T extends VersionedSchema = VersionedSchema, Versions extends T[] = []>
  implements VersionSchemaInterface<T, Versions>
{
  constructor(private versions: VersionConfiguration<T>[] = []) {}

  defineVersion<V extends T>(
    schema: V,
    migration?: Versions extends []
      ? never
      : MigrationFunction<V, Versions extends [...unknown[], infer Last] ? Last : never>,
  ): VersionSchemaInterface<T, [...Versions, V]> {
    const newVersions = [...this.versions, { schema, migration }];
    return new VersionedSchemaBuilder<T, [...Versions, V]>(newVersions);
  }

  type(): VersionedSchemaType<T, Versions> {
    const [firtSchema, secondSchema, ...remainingSchemas] = this.versions.map((v) => v.schema) as Versions;
    if (!firtSchema) {
      throw new Error('No versions defined');
    }
    if (!secondSchema) {
      return firtSchema as VersionedSchemaType<T, Versions>;
    }

    return z.union([firtSchema, secondSchema, ...remainingSchemas]) as VersionedSchemaType<T, Versions>;
  }

  parse(data: unknown): z.infer<T> {
    if (this.versions.length === 0) {
      throw new Error('No versions defined');
    }
    // Zod 4 infers .parse() return as the widened constraint bound ({ version: number })
    // rather than the generic z.infer<T>. The cast is safe because this.type() IS the
    // union of all version schemas that define T.
    return this.type().parse(data) as z.infer<T>;
  }

  migrate(object: z.infer<Versions[number]>): LatestVersion<T, Versions> {
    if (this.versions.length === 0) {
      throw new Error('No versions defined');
    }

    // Find index of version matching input object
    const currentVersionIndex = this.versions.findIndex((v) => {
      try {
        // Extract version literal from schema
        const { schema } = v;
        const versionLiteral = 'options' in schema ? schema.options[0]?.shape.version : schema.shape?.version;
        return versionLiteral?.value === object.version;
      } catch {
        return false;
      }
    });

    if (currentVersionIndex === -1) {
      throw new Error(`Invalid version ${object.version}`);
    }

    // Already at latest version
    if (currentVersionIndex === this.versions.length - 1) {
      return object as LatestVersion<T, Versions>;
    }

    // Migrate through each version
    let current: z.infer<T> = object;
    for (let i = currentVersionIndex + 1; i < this.versions.length; i++) {
      const version = this.versions[i];
      if (!version?.migration) {
        throw new Error(`Missing migration for version ${i}`);
      }
      current = (version.migration as unknown as (prev: z.infer<T>) => z.infer<T>)(current);
    }

    return current as LatestVersion<T, Versions>;
  }

  get latestVersionSchema(): z.ZodType<LatestVersion<T, Versions>> {
    const latestVersion = this.versions[this.versions.length - 1];
    if (!latestVersion) {
      throw new Error('No versions defined');
    }
    return latestVersion.schema as unknown as z.ZodType<LatestVersion<T, Versions>>;
  }
}

export function createVersionedSchema<T extends VersionedSchema>(): VersionSchemaInterface<T, []> {
  return new VersionedSchemaBuilder<T, []>();
}
